import type { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import { isNil } from "lodash";

import type { Services } from "~/core/services";

type TrackSubmitSearchKeywordParams = {
  keyword: string;
  serviceName: Services;
  countryCode: KarrotLocalCountryCode;
};
export function trackSubmitSearchKeyword({
  keyword,
  serviceName,
  countryCode,
}: TrackSubmitSearchKeywordParams) {
  if (keyword.length === 0) {
    return;
  }

  trackEvent({
    eventName: "submit_search_keyword",
    data: {
      keyword,
      service_name: serviceName,
      country_code: countryCode,
    },
  });
}

type TrackClickButtonMoveToAppParams = {
  position: string;
  countryCode: KarrotLocalCountryCode;
  serviceName?: Services;
  status?: string;
};
export function trackClickButtonMoveToApp({
  position,
  serviceName,
  countryCode,
  status,
}: TrackClickButtonMoveToAppParams) {
  trackEvent({
    eventName: "click_button_moveToApp",
    data: {
      move_to_app_button_position: position,
      country_code: countryCode,
      service_name: serviceName,
      item_status: status,
    },
  });
}

type TrackPageViewParams = {
  url: string;
  serviceName?: Services;
  countryCode: KarrotLocalCountryCode;
};
export function trackPageView({
  url,
  serviceName,
  countryCode,
}: TrackPageViewParams) {
  trackEvent({
    eventName: "$pageview",
    data: {
      $current_url: url,
      service_name: serviceName,
      country_code: countryCode,
    },
  });
}

type TrackClickMoveLoginPageParams = {
  loginButtonPosition: "GNB" | "ARTICLE_PAGE";
};
export function trackClickMoveLoginPage({
  loginButtonPosition,
}: TrackClickMoveLoginPageParams) {
  trackEvent({
    eventName: "click_move_login_page",
    data: {
      login_button_position: loginButtonPosition,
    },
  });
}

type TrackClickOpenChatWidgetParams = {
  chatWidgetPosition: "WIDGET" | "ARTICLE_PAGE";
};
export function trackClickOpenChatWidget({
  chatWidgetPosition,
}: TrackClickOpenChatWidgetParams) {
  trackEvent({
    eventName: "click_open_chatWidget",
    data: {
      chat_widget_position: chatWidgetPosition,
    },
  });
}

type TrackClickMoveArticleWritePageParams = {
  articleWriteButtonPosition: "GNB" | "ARTICLE_PAGE" | "USER_PAGE";
};
export function trackClickMoveArticleWritePage({
  articleWriteButtonPosition,
}: TrackClickMoveArticleWritePageParams) {
  trackEvent({
    eventName: "click_move_articleWritePage",
    data: {
      article_write_button_position: articleWriteButtonPosition,
    },
  });
}

type TrackSubmitCreateArticleContentParams = {
  photoCount: number;
  fleamarketArticleTradeType: "SALE" | "FREE";
  isOpenOffers: "OPEN" | "CLOSE";
};
export function trackSubmitCreateArticleContent({
  photoCount,
  fleamarketArticleTradeType,
  isOpenOffers,
}: TrackSubmitCreateArticleContentParams) {
  trackEvent({
    eventName: "submit_create_articleContent",
    data: {
      photo_count: photoCount,
      fleamarket_article_trade_type: fleamarketArticleTradeType,
      is_open_offers: isOpenOffers,
    },
  });
}

export function trackSubmitEditArticleContent() {
  trackEvent({
    eventName: "submit_edit_articleContent",
  });
}

type TrackSubmitChangeArticleStatusParams = {
  fleamarketArticleStatusType: "ON_GOING" | "RESERVED" | "CLOSED" | "HIDE";
};
export function trackSubmitChangeArticleStatus({
  fleamarketArticleStatusType,
}: TrackSubmitChangeArticleStatusParams) {
  trackEvent({
    eventName: "submit_change_articleStatus",
    data: {
      fleamarket_article_status_type: fleamarketArticleStatusType,
    },
  });
}

export function clickRequestBoostUp() {
  trackEvent({
    eventName: "click_request_boostUp",
  });
}

type TrackClickOpenMyKarrotMenuParams = {
  myKarrotMenuType: "FAVORITES" | "PURCHASES" | "RECENTLY_VIEWED";
};
export function trackClickOpenMyKarrotMenu({
  myKarrotMenuType,
}: TrackClickOpenMyKarrotMenuParams) {
  trackEvent({
    eventName: "click_open_myKarrotMenu",
    data: {
      my_karrot_menu_type: myKarrotMenuType,
    },
  });
}

export function clickSelectBuyer() {
  trackEvent({
    eventName: "click_select_buyer",
  });
}

type TrackClickChatWidgetButtonParams = {
  chatWidgetButtonType:
    | "CLOSE"
    | "EXPAND"
    | "COLLAPSE"
    | "REPORT"
    | "BLOCK_USER";
};
export function trackClickChatWidgetButton({
  chatWidgetButtonType,
}: TrackClickChatWidgetButtonParams) {
  trackEvent({
    eventName: "click_chatWidgetButton",
    data: {
      chat_widget_button_type: chatWidgetButtonType,
    },
  });
}

declare global {
  interface Window {
    dataLayer: {
      push: (args: { event: string; data?: Record<string, unknown> }) => void;
    };
  }
}

type TrackEventParams = {
  eventName: string;
  data?: Record<string, unknown>;
};
function trackEvent({ eventName, data }: TrackEventParams) {
  if (isNil(window) || isNil(window.dataLayer)) {
    console.log({
      eventName,
      data,
    });
    return;
  }

  window.dataLayer.push({
    event: eventName,
    data,
  });
}
